/**
 * ↩️ ReturnToRHModal Component
 * Fecha: 30 de octubre de 2025
 * 
 * Modal para que el Project Manager devuelva una solicitud a RH
 * cuando los salarios asignados no son adecuados.
 * Estado requerido: en_revision_salarios → devuelta_rh
 */

import { CapaDeModal } from '@/shared/components/CapaDeModal'
import React, { useState } from 'react'
import { X, CornerUpLeft, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import type { JobRequest } from '@/services/api'
import { useJobRequestWorkflow } from '@/hooks/useJobRequestWorkflow'
import {
  formatJobRequestArea,
  formatJobRequestSede
} from '@/features/rh/lib/jobRequestDisplay'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

interface ReturnToRHModalProps {
  jobRequest: JobRequest
  isOpen: boolean
  onClose: () => void
  onSuccess: (updated: JobRequest) => void
}

const ReturnToRHModal: React.FC<ReturnToRHModalProps> = ({
  jobRequest,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [motivo, setMotivo] = useState('')
  const [validationError, setValidationError] = useState('')
  const { returnToRH, loading, error } = useJobRequestWorkflow()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validación local
    if (!motivo.trim()) {
      setValidationError('Debe proporcionar un motivo para la devolución')
      return
    }

    if (motivo.trim().length < 10) {
      setValidationError('El motivo debe tener al menos 10 caracteres')
      return
    }

    const result = await returnToRH(jobRequest.id, { motivo: motivo.trim() })

    if (result) {
      toast.success('Solicitud devuelta a RH exitosamente.')
      onSuccess(result)
      handleClose()
    }
  }

  const handleClose = () => {
    if (!loading) {
      onClose()
      setMotivo('')
      setValidationError('')
    }
  }

  const handleMotivoChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMotivo(e.target.value)
    setValidationError('')
  }

  const formatCurrency = (value: string | null): string => {
    if (!value) return 'No especificado'
    const num = parseFloat(value)
    if (isNaN(num)) return 'No especificado'
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(num)
  }

  // Sugerencias de motivos comunes
  const motivosSugeridos = [
    'Los salarios propuestos están fuera del presupuesto del proyecto',
    'Se requiere ajustar el rango salarial según el mercado actual',
    'Las prestaciones necesitan ser revisadas',
    'El rango salarial no es competitivo para el perfil solicitado'
  ]

  if (!isOpen) return null

  return (
    <CapaDeModal>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <CornerUpLeft className="w-6 h-6 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Devolver a RH
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-5">
            {/* Información de la solicitud */}
            <div>
              <h3 className="font-medium text-black mb-2">
                {jobRequest.puestoSolicitar}
              </h3>
              <div className="text-sm text-black space-y-1">
                <p><strong>Departamento/Área:</strong> {formatJobRequestArea(jobRequest.area?.nombre || jobRequest.areaVacante)}</p>
                <p><strong>Sede:</strong> {formatJobRequestSede(jobRequest.sede)}</p>
              </div>
            </div>

            {/* Salarios actuales asignados */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <h4 className="font-medium text-blue-900">Salarios Actuales</h4>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-black font-medium">Salario Mínimo</p>
                  <p className="text-black text-base font-semibold">
                    {formatCurrency(jobRequest.salarioMinimo)}
                  </p>
                </div>
                <div>
                  <p className="text-black font-medium">Salario Máximo</p>
                  <p className="text-black text-base font-semibold">
                    {formatCurrency(jobRequest.salarioMaximo)}
                  </p>
                </div>
              </div>
            </div>

            {/* Advertencia */}
            <div>
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-black">
                  <p className="font-medium mb-1">Al devolver a RH:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>La solicitud cambiará a estado <strong>"Devuelta a RH"</strong></li>
                    <li>RH deberá ajustar los salarios y volver a asignar</li>
                    <li>El proceso volverá a la etapa de revisión de salarios</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Sugerencias de motivos */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motivos Comunes (Click para usar)
              </label>
              <div className="flex flex-wrap gap-2">
                {motivosSugeridos.map((sugerencia, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setMotivo(sugerencia)}
                    disabled={loading}
                    className="text-xs px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition disabled:opacity-50"
                  >
                    {sugerencia}
                  </button>
                ))}
              </div>
            </div>

            {/* Campo de motivo (OBLIGATORIO) */}
            <div>
              <label htmlFor="motivo" className="block text-sm font-medium text-gray-700 mb-2">
                Motivo de la Devolución *
              </label>
              <textarea
                id="motivo"
                value={motivo}
                onChange={handleMotivoChange}
                rows={5}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition ${
                  validationError
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-amber-500'
                }`}
                placeholder="Describe por qué se devuelve la solicitud a RH (mínimo 10 caracteres)..."
                disabled={loading}
                required
              />
              {validationError && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {validationError}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {motivo.length} caracteres (mínimo 10)
              </p>
            </div>

            {/* Error del servidor */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 sticky bottom-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className={estiloDeBoton('secundario')}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !motivo.trim()}
              className={estiloDeBoton('primario')}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Devolviendo...</span>
                </>
              ) : (
                <>
                  <CornerUpLeft className="w-4 h-4" />
                  <span>Devolver a RH</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </CapaDeModal>
  )
}

export default ReturnToRHModal
